import React from 'react';

import {
  GridRowId,
  GridColDef,
  DataGridPro,
  GridRowModes,
  GridRowModesModel,
  GridActionsCellItem,
} from '@mui/x-data-grid-pro';

import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';

import { IconButton } from '@mui/material';

import { v4 as uuid } from 'uuid';

import { observer } from 'mobx-react';

import { AssessmentResponse } from 'utils/api/modules/entities/entities.types';

import { AssessmentTabContainer, DataGridStyles } from './styles';

import { AssessmentTabProps } from './types';
import { baseColumns, emptyAssessment } from './helpers';

const AssessmentTab: React.FC<AssessmentTabProps> = ({ store }) => {
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
  const [rows, setRows] = React.useState<AssessmentResponse[]>(store.assessments || []);

  const handleAddRowClick = (): void => {
    const id = uuid();
    setRows((oldRows) => [{ ...emptyAssessment, uuid: id }, ...oldRows]);
    setRowModesModel((oldModel) => ({
      [id]: { mode: GridRowModes.Edit, fieldToFocus: 'hazard' },
      ...oldModel,
    }));
  };

  const handleDeleteClick = (id: GridRowId) => async () => {
    const success = await store.deleteAssessment(id as string);
    if (success) {
      setRows(rows.filter((row) => row.uuid !== id));
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    const { isNew } = rows.find((row) => row.uuid === id) as AssessmentResponse & { isNew: boolean };
    // if the row is new, remove it from the rows
    if (isNew) {
      setRows(rows.filter((row) => row.uuid !== id));
    }

    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const handleRowUpdateError = (rowId: string): never => {
    setRowModesModel((prevModel) => ({
      ...prevModel,
      [rowId]: { mode: GridRowModes.Edit },
    }));
    throw new Error('API update failed');
  };

  const handleNewRowUpdate = async (newRow: AssessmentResponse): Promise<AssessmentResponse> => {
    const newAssessments = await store.addAssessment(newRow);
    if (newAssessments.length === 0) {
      handleRowUpdateError(newRow.uuid);
    }
    setRows(newAssessments);
    return newRow;
  };

  const handleExistingRowUpdate = async (newRow: AssessmentResponse): Promise<AssessmentResponse> => {
    const success = await store.editAssessment(newRow.uuid, newRow);
    if (!success) {
      handleRowUpdateError(newRow.uuid);
    }
    return newRow;
  };

  const processRowUpdate = async (newRow: AssessmentResponse): Promise<AssessmentResponse> => {
    const { isNew } = newRow as AssessmentResponse & { isNew: boolean };

    if (!newRow.uuid) {
      return newRow;
    }

    if (isNew) {
      return handleNewRowUpdate(newRow);
    }

    return handleExistingRowUpdate(newRow);
  };

  const columns: GridColDef[] = [
    ...baseColumns,
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      cellClassName: 'actions',
      disableColumnMenu: true,
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              label="Save"
              icon={<SaveIcon />}
              style={{ padding: 0 }}
              onClick={() =>
                setRowModesModel((prevModel) => ({
                  ...prevModel,
                  [id]: { mode: GridRowModes.View },
                }))
              }
            />,
            <GridActionsCellItem
              label="Cancel"
              icon={<CancelIcon />}
              style={{ padding: 0 }}
              onClick={handleCancelClick(id)}
            />,
          ];
        }

        return [
          <GridActionsCellItem style={{ padding: 0 }} icon={<EditIcon />} label="Edit" onClick={handleEditClick(id)} />,
          <GridActionsCellItem
            label="Delete"
            style={{ padding: 0 }}
            icon={<DeleteIcon />}
            onClick={handleDeleteClick(id)}
          />,
        ];
      },
    },
  ];

  return (
    <AssessmentTabContainer>
      <IconButton
        color="primary"
        sx={{ fontWeight: '700', gap: '8px', margin: '10px', borderRadius: 0 }}
        onClick={handleAddRowClick}
      >
        <AddCircleIcon />
        Add New Row
      </IconButton>
      <DataGridPro
        rowHeight={100}
        hideFooter
        rows={rows}
        editMode="row"
        columns={columns}
        sx={DataGridStyles}
        getRowId={(row) => row.uuid}
        rowModesModel={rowModesModel}
        loading={store.loadingAssessments}
        processRowUpdate={(newRow) => processRowUpdate(newRow)}
        initialState={{ pinnedColumns: { right: ['actions'] } }}
      />
    </AssessmentTabContainer>
  );
};

export default observer(AssessmentTab);
